import { useState, useEffect } from 'react'
import { Copy, Download, Share2, Check, Wallet } from 'lucide-react'
import { Capacitor } from '@capacitor/core'
import { Share as CapShare } from '@capacitor/share'
import { generateQRDataURL } from '../utils/qrcode.jsx'
import { generateVisitPrepPDF } from '../utils/visitPrepPDF'
import SharePassModal from './SharePassModal'
import { buildClaimUrl } from '../utils/claimUrl.js'
import { useCaseStore, useProfileStore, useUserStore } from '../stores'
import { casesAPI } from '../services/api'
import { FacesheetUnavailableError, FacesheetDeniedError, facesheetReadRefused } from '../utils/facesheet'
import { useI18n } from '../i18n'
import { OrellaSpinner } from './OrellaLoader' // s123: the app's one spinner

/**
 * VisitPrepCodeDisplay v155.0.2
 * 
 * Redesigned: code-first hero, QR behind toggle, 2-button action row,
 * compact doctor card. ★ The "Case Home" CTA moved to DiagnoseScreen's ready block,
 * BELOW the doctor and appointment cards — his layout call, 2026-09-11. The
 * vpShimmer/vpPulse live in index.css, because BOTH files use them and a keyframe in
 * a component's own <style> is global only while that component is mounted.
 */

export default function VisitPrepCodeDisplay({ shareCode, expirationDate }) {
  const { t } = useI18n()
  const [copied, setCopied] = useState(false)
  const [showSharePass, setShowSharePass] = useState(false)
  const [qrDataUrl, setQrDataUrl] = useState(null)
  const { currentCase } = useCaseStore()
  const profile = useProfileStore((state) => state.profile)
  const user = useUserStore((state) => state.user)

  const claimUrl = buildClaimUrl(shareCode)
  const externalDoctor = currentCase?.externalDoctor || currentCase?.external_doctor || null

  // Pass fields
  const _id = profile?.identification || {}
  const passPatientName = [_id.firstName, _id.lastName].filter(Boolean).join(' ')
    || [user?.first_name, user?.last_name].filter(Boolean).join(' ') || ''
  const passCaseLine = [
    currentCase?.specialtyName || currentCase?.specialty,
    currentCase?.focusAreaName || currentCase?.focus_area,
  ].filter(Boolean).join(' · ')

  useEffect(() => {
    if (shareCode) {
      generateQRDataURL(claimUrl, { size: 200, color: '#15354F' })
        .then(url => { if (url) setQrDataUrl(url) })
        .catch(() => {})
    }
  }, [shareCode, claimUrl])

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareCode)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch { console.log('Copy failed') }
  }

  // ★★★ v242.87.0 (TF b34 #14) — this was not awaited.
  //   generateVisitPrepPDF is async (it renders HTML, hands it to
  //   @capgo/capacitor-pdf-generator, writes the result to Directory.Cache and
  //   opens the share sheet). Fire-and-forget meant a plugin rejection became an
  //   unhandled rejection: no sheet, no error, no spinner — a button that did
  //   nothing at all. Against a neighbouring button that always opened a sheet,
  //   "they do the same thing" is a generous reading of what he saw.

  // ★★★ v242.145.0 PVS-FACESHEET-CLIENT-SOURCE-1 — ASK, DO NOT REMEMBER.
  //     The server re-reads `profiles` + `users` on every call; this store is
  //     sessionStorage-backed and is empty after the WKWebView recycle that the
  //     share sheet triggers. So the server answers, and the store is only a
  //     fallback for a device that cannot reach it. If NEITHER can name the
  //     patient, no document is produced — the generator throws and we say so.
  const resolveFacesheet = async () => {
    try {
      const res = await casesAPI.getFacesheet(currentCase?.id)
      if (res?.facesheet) return res.facesheet
    } catch (err) {
      // ★ P2 Release 3 (P2-4): a 403 is the server refusing this case to this account, not
      //   an outage. No document is built from the local store over that answer.
      if (facesheetReadRefused(err)) throw new FacesheetDeniedError()
      console.warn('[VisitPrep] facesheet fetch failed, falling back to the local profile:', err)
    }
    return null
  }

  const [pdfBusy, setPdfBusy] = useState(false)
  const [shareBusy, setShareBusy] = useState(false)
  const [pdfError, setPdfError] = useState('')
  const handleDownloadPDF = async () => {
    if (pdfBusy) return
    setPdfBusy(true)
    setPdfError('')
    try {
      await generateVisitPrepPDF({
        currentCase,
        facesheet: await resolveFacesheet(),
        profile,
        shareCode,
        qrDataUrl,
        externalDoctor,
        preVisitSummary: currentCase?.preVisitSummary || currentCase?.pre_visit_summary,
        userPhone: user?.phone || '',
        userEmail: user?.email || '',
      })
    } catch (err) {
      console.error('[VisitPrep] PDF generation failed:', err)
      // ★ A refusal is not a crash and must not read like one. The patient can
      //   fix this (reconnect, or finish their profile); a generic "try again"
      //   would send them round the same loop for ever.
      setPdfError(err instanceof FacesheetDeniedError
        ? t('visitPrep.facesheetDenied', "This case is not available to your account, so no document was created. If you switched accounts, sign in with the account this case belongs to.")
        : err instanceof FacesheetUnavailableError
        ? t('visitPrep.facesheetUnavailable',
            "We couldn't load your profile details, so the document would have gone to your doctor with no patient information on it. Check your connection and try again.")
        : t('visitPrep.pdfFailed', "Couldn't build the PDF. Please try again."))
    } finally {
      setPdfBusy(false)
    }
  }

  // ★★★ v242.88.0 (TF b34 #14) — SHARE SENDS THE DOCUMENT NOW.
  //   Dr. Rami: "The share function should allow user to email or text the
  //   document to a recipient" — and, on whether the code rides along, "yes
  //   keep the share code so the MD side can easily claim it."
  //   It used to share TEXT AND A URL and nothing else, so the eleven-page
  //   hand-off the patient had just generated was attached to neither the email
  //   nor the message. Now the PDF is generated and shared WITH the code in the
  //   body: Mail attaches it, Messages sends it as a document, and the doctor
  //   still has the code to claim the live case.
  //   ★ On iOS both this and Save PDF end in UIActivityViewController — that is
  //     the only outbound mechanism the platform has. What differs is the
  //     PAYLOAD: Save hands over a file, this hands over a file AND a message.
  //     If they still read as one thing on device, the answer is one button
  //     with a destination picker, not two that differ by payload.
  const handleShare = async () => {
    const shareBody = `Hi — I've prepared my case details for you using Orella Health.\n\n`
      + `The full hand-off document is attached.\n\n`
      + `Share code: ${shareCode}${expirationDate ? ` (expires ${expirationDate})` : ''}\n`
      + `Claim the live case: ${claimUrl}`
    const payload = {
      title: t('visitPrep.orellaVisitPrep'),
      text: shareBody,
      url: claimUrl,
    }
    if (Capacitor?.isNativePlatform?.()) {
      if (shareBusy) return
      setShareBusy(true)
      setPdfError('')
      try {
        await generateVisitPrepPDF({
          currentCase,
          facesheet: await resolveFacesheet(),
          profile,
          shareCode,
          qrDataUrl,
          externalDoctor,
          preVisitSummary: currentCase?.preVisitSummary || currentCase?.pre_visit_summary,
          userPhone: user?.phone || '',
          userEmail: user?.email || '',
          shareText: shareBody,
          shareTitle: t('visitPrep.orellaVisitPrep'),
        })
      } catch (err) {
        console.error('[VisitPrep] share failed:', err)
        if (err instanceof FacesheetDeniedError) {
          // ★ P2 Release 3 (P2-4): the server refused this case to this account. Sharing the
          //   code instead would hand out a case the server just said is not ours to share.
          // the finally block below clears shareBusy
          setPdfError(t('visitPrep.facesheetDenied', "This case is not available to your account, so no document was created. If you switched accounts, sign in with the account this case belongs to."))
          return
        }
        if (err instanceof FacesheetUnavailableError) {
          // ★ DO NOT fall back to sharing the code here. The fallback exists so
          //   the MD can still claim the case when the PDF fails; it must not
          //   quietly succeed when the reason we stopped is that we do not know
          //   who the patient is.
          // the finally block below clears shareBusy
          setPdfError(t('visitPrep.facesheetUnavailable',
            "We couldn't load your profile details, so the document would have gone to your doctor with no patient information on it. Check your connection and try again."))
          return
        }
        // Falling back to a code-and-link share is better than nothing: the MD
        // can still claim the case, they just have to open it themselves.
        setPdfError(t('visitPrep.shareFellBack', "Couldn't attach the PDF — sharing the code instead."))
        try { await CapShare.share(payload) } catch (_) { /* dismissed */ }
      } finally {
        setShareBusy(false)
      }
      return
    }
    if (navigator.share) {
      try {
        await navigator.share(payload)
      } catch { /* cancelled */ }
    } else {
      // Fallback: open email
      const subject = encodeURIComponent('Case Report — Orella Health')
      const body = encodeURIComponent(
        `Hi,\n\nI've prepared my case details for you using Orella Health.\n\nShare Code: ${shareCode}\n${expirationDate ? `Expires: ${expirationDate}\n` : ''}\nYou can view my complete case at:\n${claimUrl}\n\nThank you!`
      )
      window.open(`mailto:?subject=${subject}&body=${body}`)
    }
  }

  return (
    <div className="space-y-3">

      {/* ===== HERO: Share code ===== */}
      <div className="bg-white rounded-2xl border-[1.5px] border-gray-200 overflow-hidden">
        <div className="px-5 pt-5 pb-4 text-center">
          <p className="text-[10px] font-bold uppercase tracking-[1.5px] text-gray-400 mb-2.5">{t('visitPrep.yourShareCode')}</p>
          <div className="flex items-center justify-center gap-2.5 mb-1">
            <span className="text-[28px] font-mono font-extrabold tracking-[4px]" style={{ color: '#15354F' }}>
              {shareCode}
            </span>
            <button
              onClick={handleCopy}
              className="p-2 rounded-lg active:scale-90 transition-all"
              style={{ background: '#E9F8FF' }}
            >
              {copied ? <Check size={16} className="text-green-500" /> : <Copy size={16} className="text-[#46A8F9]" />}
            </button>
          </div>

          {expirationDate && (
            <p className="text-[10px] text-gray-400 mb-3">{t('visitPrep.expires', 'Expires')} {expirationDate}</p>
          )}

          {/* Visit pass — opens the wallet-style pass (like check-in) */}
          <button
            onClick={() => setShowSharePass(true)}
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold active:opacity-70 transition-opacity"
            style={{ color: '#46A8F9' }}
          >
            <Wallet size={14} />
            {t('visitPrep.showVisitPass')}
          </button>
        </div>
      </div>

      {/* ===== ACTION ROW: Save/Download PDF + Share with doctor ===== */}
      {/* ★ v242.87.0 (TF b34 #14) — relabelled on native. There is no silent
          "download" on iOS: the share sheet's "Save to Files" IS the download,
          and calling the button Download while the neighbouring button also
          opens a share sheet is what made the two indistinguishable. The
          difference is real — this one attaches a generated PDF, the other
          shares a code and a link — it just was not sayable in one word. */}
      <div className="flex gap-2.5">
        <button
          onClick={handleDownloadPDF}
          disabled={pdfBusy}
          className="flex-1 py-3 rounded-xl font-semibold text-[12px] flex items-center justify-center gap-1.5 text-white active:scale-[0.97] transition-all disabled:opacity-60"
          style={{ background: '#15354F' }}
        >
          {pdfBusy ? <OrellaSpinner size={16} tone="inherit" /> : <Download size={14} />}
          {pdfBusy
            ? t('visitPrep.buildingPdf', 'Building PDF…')
            : (Capacitor?.isNativePlatform?.()
                ? t('visitPrep.savePdf', 'Save PDF')
                : t('visitPrep.downloadPdf', 'Download PDF'))}
        </button>
        <button
          onClick={handleShare}
          disabled={shareBusy}
          className="flex-1 py-3 rounded-xl font-semibold text-[12px] flex items-center justify-center gap-1.5 border-[1.5px] active:scale-[0.97] transition-all disabled:opacity-60"
          style={{ color: '#15354F', borderColor: '#E5E7EB' }}
        >
          {shareBusy ? <OrellaSpinner size={16} tone="inherit" /> : <Share2 size={14} />}
          {shareBusy
            ? t('visitPrep.preparingDoc', 'Preparing…')
            : t('visitPrep.shareWithDoctor', 'Share with doctor')}
        </button>
      </div>
      {pdfError && <p className="text-[11px] text-red-500 text-center">{pdfError}</p>}

      {/* ===== COMPACT DOCTOR CARD ===== */}
      {externalDoctor?.name && (
        <div className="bg-white rounded-xl border-[1.5px] border-gray-200 overflow-hidden">
          <div className="flex items-center gap-2.5 px-3.5 py-2.5">
            <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#D1FAE5' }}>
              <Check size={11} className="text-green-600" strokeWidth={2.5} />
            </div>
            <p className="text-[12px] font-semibold" style={{ color: '#15354F' }}>{externalDoctor.name}</p>
            {externalDoctor.phone && (
              <p className="text-[11px] text-gray-400 ml-auto">{externalDoctor.phone}</p>
            )}
          </div>
        </div>
      )}

      <SharePassModal
        isOpen={showSharePass}
        onClose={() => setShowSharePass(false)}
        patientName={passPatientName}
        caseLine={passCaseLine}
        shareCode={shareCode}
        expiresLabel={expirationDate}
        qrValue={claimUrl}
        instruction={t('visitPrep.scanToOpenYourCase')}
      />
    </div>
  )
}
